import {
  computeBBM,
  computeBDR,
  computeQAS,
} from '../../computations/labComputations'
import { useFormContext, useWatch } from 'react-hook-form'
import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'

import './labQuantitySection.less'

// Rows shared across all three sub-columns
const QUANTITY_ROWS = [
  { key: 'volume15', labelKey: 'rob.survey.bunker.lab.quantity.volume15' },
  { key: 'density15', labelKey: 'rob.survey.bunker.lab.quantity.density15' },
  {
    key: 'specGravity',
    labelKey: 'rob.survey.bunker.lab.quantity.specGravity',
  },
  { key: 'massMtVac', labelKey: 'rob.survey.bunker.lab.quantity.massMtVac' },
  {
    key: 'weightMtAir',
    labelKey: 'rob.survey.bunker.lab.quantity.weightMtAir',
  },
]

// Strip trailing zeros after decimal, keeping at least one decimal place.
// e.g. "123.000" → "123.0",  "123.450" → "123.45",  "123.456" → "123.456"
const fmt = v => {
  if (v === null || v === undefined || v === '—') return v ?? '—'
  const n = Number(v)
  if (isNaN(n)) return v

  return n % 1 === 0 ? n.toFixed(1) : String(n)
}

const ReadOnlyCell = ({ value }) => (
  <span className="lab-qty__readonly">{value}</span>
)

ReadOnlyCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

const LabQuantitySection = () => {
  const { t } = useTranslation()
  const { control } = useFormContext()

  const bdnDensity15 = useWatch({ control, name: 'bunker.bdn.density15' })
  const bdnVolume15 = useWatch({ control, name: 'bunker.bdn.volume15' })
  const bargeVolume15 = useWatch({ control, name: 'bunker.barge.volume15' })
  const labDensity15 = useWatch({ control, name: 'laboratory.tests.density15' })

  const bdrValues = computeBDR(bdnDensity15, bdnVolume15)
  const bbmValues = computeBBM(bdnDensity15, bargeVolume15)
  const qasValues = computeQAS(labDensity15, bargeVolume15, bdnVolume15)

  return (
    <div className="lab-card lab-qty">
      {/* Three sub-column headers */}
      <div className="lab-qty__header-row">
        <div className="lab-qty__col-header lab-qty__col-header--bdr">
          <div className="lab-qty__col-title">
            {t('rob.survey.bunker.lab.quantity.bdrTitle')}
          </div>
          <div className="lab-qty__col-subtitle">
            {t('rob.survey.bunker.lab.quantity.bdrSubtitle')}
          </div>
        </div>
        <div className="lab-qty__col-header lab-qty__col-header--bbm">
          <div className="lab-qty__col-title">
            {t('rob.survey.bunker.lab.quantity.bbmTitle')}
          </div>
          <div className="lab-qty__col-subtitle">
            {t('rob.survey.bunker.lab.quantity.bbmSubtitle')}
          </div>
        </div>
        <div className="lab-qty__col-header lab-qty__col-header--qas">
          <div className="lab-qty__col-title">
            {t('rob.survey.bunker.lab.quantity.qasTitle')}
          </div>
          <div className="lab-qty__col-subtitle">
            {t('rob.survey.bunker.lab.quantity.qasSubtitle')}
          </div>
        </div>
      </div>

      {/* Data rows */}
      <div className="lab-card__body">
        {QUANTITY_ROWS.map(({ key, labelKey }) => (
          <div className="lab-qty__row" key={key}>
            {/* BDR — from Bunker tab */}
            <div className="lab-qty__cell lab-qty__cell--readonly">
              <span className="lab-qty__row-label">{t(labelKey)}</span>
              <ReadOnlyCell value={bdrValues ? fmt(bdrValues[key]) : '—'} />
            </div>
            {/* BBM — density from BDN, volume from Barge Tank tab */}
            <div className="lab-qty__cell lab-qty__cell--readonly">
              <span className="lab-qty__row-label">{t(labelKey)}</span>
              <ReadOnlyCell value={bbmValues ? fmt(bbmValues[key]) : '—'} />
            </div>
            {/* QAS — lab density × barge volume (fallback to BDN volume) */}
            <div className="lab-qty__cell lab-qty__cell--readonly">
              <span className="lab-qty__row-label">{t(labelKey)}</span>
              <ReadOnlyCell value={qasValues ? fmt(qasValues[key]) : '—'} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default LabQuantitySection
